import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PROFILEIMAGE from "../assets/ProfilePicture.png";
import Footer from "../Components/Footer";
import ContactInfo from "../Components/ContactInfo";
import ListingsCard from "../Components/ListingsCard";
import HomePageNavBar from "../Components/HomePageNavBar";
import { getProfile } from "../Services/usersApi";
import { getPreferredListings } from "../Services/listingsApi";
import RegionColors from "../Components/RegionColors";
import { ClipLoader } from "react-spinners";

const ViewProfile = () => {
    window.scrollTo(0, 0);
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const navigateTo = (path) => {
        if (path) {
            navigate(path);
        }
    };

    const [profileImageLoaded, setProfileImageLoaded] = useState(false);

    useEffect(() => {
        document.title = process.env.REACT_APP_REGION_NAME;
    }, []);

    const [user, setUser] = useState(null);
    const [listings, setListings] = useState([]);

    useEffect(() => {
        const myUserId = window.localStorage.getItem("userId");
        if (userId) {
            getProfile(userId)
                .then((response) => {
                    const userData = response.data.data;
                    if (userData) {
                        setUser(userData);
                        if (myUserId) {
                            getPreferredListings(myUserId)
                                .then((listingsResponse) => {
                                    setListings(listingsResponse.data.data || []);
                                })
                                .catch(() => navigate("/Error"));
                        } else {
                            navigate("/Error");
                        }
                    } else {
                        navigate("/Error");
                    }
                })
                .catch(() => navigate("/Error"));
        } else {
            navigate("/Error");
        }
    }, [userId, navigate]);

    function goBack() {
        navigateTo(`/`);
    }

    return (
        <section className="text-gray-600 bg-white body-font">
            <HomePageNavBar showBlackLogo/>

            <div className="profile-page bg-gray-100">
                <div className="relative h-80 md:h-96 w-full">
                    <div
                        className="absolute inset-0"
                        style={{ backgroundColor: RegionColors.lightBgColorV2 }}
                    >
                        <span className="absolute inset-0 bg-black opacity-50"></span>
                    </div>
                </div>

                <div className="relative px-5 md:px-10 lg:px-[5rem] xl:px-[10rem] 2xl:px-[20rem] py-6">
                    <div className="max-w-5xl mx-auto">
                        <div className="relative flex flex-col bg-white w-full shadow-lg rounded-xl -mt-24 p-6 md:p-8">
                            <div className="flex flex-col items-center">
                                <div className="relative -mt-28 w-40 h-40 md:w-48 md:h-48 transform transition-transform duration-300 hover:scale-105 hover:-translate-y-1 z-0">
                                    <div className="absolute inset-0 shadow-xl rounded-full" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}></div>
                                    {!profileImageLoaded && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-full animate-pulse">
                                            <ClipLoader size={24} color="#36D7B7" />
                                        </div>
                                    )}
                                    <img
                                        onClick={() => navigateTo(`/ViewProfile/${userId}`)}
                                        src={user?.image || PROFILEIMAGE}
                                        alt={user?.name || "User"}
                                        onLoad={() => setProfileImageLoaded(true)}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = PROFILEIMAGE;
                                            setProfileImageLoaded(true);
                                        }}
                                        className={`rounded-full cursor-pointer object-cover w-full h-full transition-opacity duration-300 ${profileImageLoaded ? "opacity-100" : "opacity-0"}`}
                                    />
                                </div>

                                <p className="text-gray-500 font-medium text-sm mt-4 md:text-base">
                                    <i className="fas fa-map-marker-alt"></i>
                                    {user?.username}
                                </p>

                                {user?.metadata?.description && (
                                    <p className="text-gray-700 text-sm md:text-base mt-4 leading-relaxed max-w-3xl">
                                        {user.metadata.description}
                                    </p>
                                )}
                            </div>

                            <div className="mt-6 md:mt-10 border-t border-gray-200">
                                <ContactInfo user={user} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white px-5 md:px-10 lg:px-[5rem] xl:px-[10rem] 2xl:px-[20rem] py-6 mt-0 mb-10 space-y-10 flex flex-col">
                <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-800 md:text-2xl dark:text-slate-800">
                    {t("profileEntries")}
                </h1>
                {listings.length > 0 ? (
                    <div className="relative place-items-center bg-white mb-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 justify-start">
                        {listings
                            .map((listing, key) => (
                                <ListingsCard listing={listing} key={key} />
                            ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center min-h-[40vh] px-6">
                        <h2 className="mb-6 text-4xl font-bold">
                            {t("currently_no_listings")}
                        </h2>
                        <p className="text-lg text-gray-600">
                            {t("selectOtherCityorCategory")}
                        </p>
                        <button
                            onClick={goBack}
                            className="mt-10 px-6 py-3 bg-gray-700 text-white rounded-full hover:scale-105 transition"
                        >
                            {t("goBack")}
                        </button>
                    </div>
                )}
            </div>

            <div className="bottom-0 w-full">
                <Footer />
            </div>
        </section>
    );
};

export default ViewProfile;